'use client';

import { CheckCircle2, Clock, MessageSquare, Phone, ArrowRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SuccessPanelProps {
  leadId: string;
  serviceType: string;
  city: string;
  fullName: string;
  onSubmitAnother: () => void;
}

const NEXT_STEPS = [
  {
    icon: Clock,
    title: 'We\'re matching your request',
    description: 'Our system is finding verified professionals in your area who handle this service.',
    timing: 'Right now',
  },
  {
    icon: Phone,
    title: 'Expect a call or text',
    description: 'Matched professionals will reach out to discuss your project and provide a quote.',
    timing: 'Within 2 hours',
  },
  {
    icon: MessageSquare,
    title: 'Compare and choose',
    description: 'You\'re under no obligation. Review quotes and choose the best fit for your needs.',
    timing: 'At your pace',
  },
];

export default function SuccessPanel({
  leadId,
  serviceType,
  city,
  fullName,
  onSubmitAnother,
}: SuccessPanelProps) {
  const firstName = fullName.split(' ')[0] || fullName;

  return (
    <div className="animate-scale-in">
      {/* Success header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-4 animate-success-check">
          <CheckCircle2 className="w-9 h-9 text-success" aria-hidden="true" />
        </div>
        <h2 className="text-2xl font-700 text-foreground tracking-tight mb-2">
          Request received, {firstName}!
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">
          We&apos;re matching you with verified <strong className="text-foreground font-600">{serviceType}</strong> professionals in <strong className="text-foreground font-600">{city}</strong>.
        </p>
      </div>

      {/* Reference number */}
      <div className="flex items-center justify-center gap-2 mb-7">
        <span className="text-xs text-muted-foreground font-500">Reference #</span>
        <code className="font-mono text-xs bg-muted px-2.5 py-1 rounded-lg text-foreground tracking-wide border border-border">
          {leadId.slice(0, 8).toUpperCase()}
        </code>
      </div>

      {/* Next steps */}
      <div className="space-y-0 mb-7">
        <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">
          What happens next
        </p>
        <div className="space-y-0">
          {NEXT_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isLast = index === NEXT_STEPS.length - 1;
            return (
              <div key={step.title} className="flex gap-3.5">
                {/* Timeline connector */}
                <div className="flex flex-col items-center">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                    <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
                  </div>
                  {!isLast && (
                    <div className="w-px flex-1 bg-border mt-1 mb-1 min-h-[20px]" aria-hidden="true" />
                  )}
                </div>
                {/* Step content */}
                <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-4'}`}>
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="text-sm font-600 text-foreground">{step.title}</p>
                    <span className="flex-shrink-0 text-xs text-primary font-500 bg-primary/8 px-2 py-0.5 rounded-full">
                      {step.timing}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tip box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5 mb-6">
        <p className="text-xs text-amber-800 font-500 leading-relaxed">
          <strong className="font-700">Tip:</strong> Keep your phone nearby. Professionals typically call from local numbers within the first hour.
        </p>
      </div>

      {/* Submit another */}
      <button
        type="button"
        onClick={onSubmitAnother}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-border text-sm font-600 text-muted-foreground hover:text-foreground hover:border-foreground/30 hover:bg-muted/50 transition-all duration-150 active:scale-[0.98]"
        aria-label="Submit another service request"
      >
        Submit another request
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}